import { useEffect, useRef } from "react";
import {
  motion,
  motionValue,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  CITIES,
  BOOKING_URL,
  ADVISORY_INTRO,
  FEE_NOTE,
  FEES,
  TITLE_LINES,
} from "../data/site";
import { scrollToTarget, scrollToY } from "../lib/lenis";
import { Countdown } from "./Countdown";
import { useCurrentFair } from "../lib/fairSchedule";

const EASE = [0.16, 1, 0.3, 1];
const STACK_MAX_WIDTH = 900;

const STRIP_H = 44;

/* Phone "deck": each city is a sticky full-screen card pinned STRIP_H lower than the one before.
   As the next card slides up over it, the card beneath dims and its top edge becomes a thin
   "01 ABU DHABI" strip that stays pinned, so you always see where you are.
   Progress for card i = how far card i+1 has entered the viewport. */
function useStackProgress(count) {
  const cardRefs = useRef(Array.from({ length: count + 1 }, () => ({ current: null }))).current;
  const progress = useRef(Array.from({ length: count }, () => motionValue(0))).current;
  const { scrollY } = useScroll();

  const update = () => {
    const stacking = window.innerWidth <= STACK_MAX_WIDTH;
    const vh = window.innerHeight;
    progress.forEach((mv, i) => {
      const next = cardRefs[i + 1].current;
      if (!stacking || !next) {
        mv.set(0);
        return;
      }
      // card i+1 travels from the bottom of the screen to its pinned slot just below card i's strip
      const top = next.getBoundingClientRect().top;
      const travel = vh - (i + 1) * STRIP_H;
      mv.set(Math.min(1, Math.max(0, (vh - top) / travel)));
    });
  };

  useMotionValueEvent(scrollY, "change", update);
  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { cardRefs, progress };
}

function CityColumn({ city, i, mx, my, progress, cardRef, isCurrent }) {
  const dim = useTransform(progress, [0, 1], [0, 0.55]);
  const strip = useTransform(progress, [0.55, 0.95], [0, 1]);
  const stripY = useTransform(progress, [0.55, 0.95], [-STRIP_H, 0]);
  const stripEvents = useTransform(progress, (p) => (p > 0.8 ? "auto" : "none"));
  const jumpBack = () => {
    const el = cardRef.current;
    if (!el || window.innerWidth > STACK_MAX_WIDTH) return;
    // Sticky cards report their pinned position, so derive the natural one from the container:
    // cards are equal-height siblings at the top of .column-container.
    const container = el.parentElement;
    const containerTop = container.getBoundingClientRect().top + window.scrollY;
    const cardHeight = el.getBoundingClientRect().height;
    scrollToY(containerTop + i * cardHeight - i * STRIP_H);
  };
  return (
    <motion.section
      ref={cardRef}
      className="column city"
      style={{ "--stack-top": `${i * STRIP_H}px` }}
      tabIndex={0}
      aria-label={`${city.name}: ${city.fair}, ${city.dates}`}
      data-testid={`city-column-${city.id}`}
    >
      <motion.div
        className="parallax-wrap"
        style={{ x: mx, y: my }}
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.15 + i * 0.12, ease: EASE }}
      >
        <img
          src={city.image}
          alt={city.alt}
          className="column-image"
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : "auto"}
          decoding="async"
          width="1600"
          height="1000"
        />
      </motion.div>
      <div className="overlay"></div>
      <div className="column-content">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 + i * 0.12, ease: EASE }}
        >
          <span className="idx">
            {city.index} / {city.country} · {city.date}
          </span>
          <h2 className="serif vertical-title">{city.name}</h2>
        </motion.div>
        <div className="column-details">
          <p className="serif text-2xl md:text-3xl mb-3">{city.fair}</p>
          <div className="text-[12px] font-bold uppercase tracking-widest space-y-1.5 text-deep">
            <p>{city.dates}</p>
            {city.preview ? (
              <p className="text-accent" data-testid={`city-preview-${city.id}`}>
                {city.preview}
              </p>
            ) : null}
            <p>{city.venue}</p>
          </div>
          {isCurrent ? (
            <span className="inline-flex flex-wrap items-center gap-2 mt-3.5">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener"
                className="badge !mt-0 pointer-events-auto hover:bg-deep transition-colors"
                data-testid={`city-badge-link-${city.id}`}
              >
                Now booking
              </a>
              <Countdown className="!ml-0 py-[7px] px-3 plate-countdown" testId="fair-countdown-plate" />
            </span>
          ) : null}
        </div>
      </div>
      <motion.div className="stack-dim" style={{ opacity: dim }} aria-hidden="true" />
      <motion.button
        type="button"
        className="stack-strip"
        style={{ opacity: strip, y: stripY, pointerEvents: stripEvents }}
        onClick={jumpBack}
        tabIndex={-1}
        aria-label={`Back to ${city.name}`}
        data-testid={`city-strip-${city.id}`}
      >
        <span className="serif">{city.index}</span>
        <span>{city.name}</span>
        <span className="stack-strip-meta">{city.date}</span>
      </motion.button>
    </motion.section>
  );
}

function AdvisoryPanel({ panelRef }) {
  return (
    <section className="column advisory bg-paper" data-testid="advisory-panel" ref={panelRef}>
      <div className="column-content !p-8 xl:!p-10">
        <div className="h-full flex flex-col">
          <motion.span
            className="text-[11px] font-medium tracking-widest uppercase text-ink mb-5"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
          >
            The Advisory
          </motion.span>

          <h1
            className="serif advisory-title uppercase tracking-normal text-ink"
            data-testid="advisory-title"
          >
            {TITLE_LINES.map((line, i) => (
              <span className="mask-line" key={i}>
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            className="advisory-body flex-1 overflow-y-auto pr-1 min-h-0"
            data-lenis-prevent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <p className="text-base leading-relaxed mb-6 text-deep font-medium max-w-[40ch]">{ADVISORY_INTRO}</p>

            <span className="text-[10px] uppercase font-medium tracking-widest text-ink">
              Service fee
            </span>
            <div className="mt-2 text-xs" data-testid="fee-table">
              {FEES.map((fee) => (
                <div className="fee-row" key={fee.label}>
                  <span className="uppercase tracking-widest text-[12px] text-ink">{fee.label}</span>
                  <span className="serif text-xl text-ink">{fee.price}</span>
                </div>
              ))}
            </div>
            <p className="text-sm font-medium leading-relaxed mt-4 text-deep flex items-start gap-3" data-testid="fee-note">
              <span className="text-deep text-3xl leading-none shrink-0 font-sans" aria-hidden="true">✱</span>
              <span>{FEE_NOTE}</span>
            </p>
            <a
              href="#included"
              onClick={(e) => {
                e.preventDefault();
                scrollToTarget("#included");
              }}
              className="inline-block mt-4 text-[11px] uppercase font-medium tracking-widest text-ink border-b border-ink/40 pb-0.5"
              data-testid="whats-included-link"
            >
              What's included ↓
            </a>
          </motion.div>

          <motion.a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener"
            data-testid="advisory-plan-trip-cta"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
            className="mt-5 bg-deep text-greige px-6 py-4 flex justify-between items-center group/cta hover:opacity-80 transition-opacity"
          >
            <span className="text-[12px] uppercase font-bold tracking-widest">Plan your trip</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-greige transition-transform group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1"
              aria-hidden="true"
            >
              <line x1="7" y1="17" x2="17" y2="7"></line>
              <polyline points="7 7 17 7 17 17"></polyline>
            </svg>
          </motion.a>
        </div>
      </div>
    </section>
  );
}

export const HeroColumns = () => {
  const ref = useRef(null);
  const { cardRefs, progress } = useStackProgress(CITIES.length);
  const { fair: current } = useCurrentFair();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <main
      className="column-container"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="hero-columns"
    >
      {CITIES.map((city, i) => (
        <CityColumn
          key={city.id}
          city={city}
          i={i}
          mx={sx}
          my={sy}
          progress={progress[i]}
          cardRef={cardRefs[i]}
          isCurrent={current?.cityId === city.id}
        />
      ))}
      <AdvisoryPanel panelRef={cardRefs[CITIES.length]} />
    </main>
  );
};
